import { RefObject, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/lib/Auth/authStore";
import {
  getQueryKey,
  useGqlMutation,
  useGqlQuery,
  useOrgId,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  RelocationDealChatMessage,
  relocationDealChatQuery,
} from "@/app/Relocations/GraphQL/relocationDealChatQuery";
import { refreshRelocationDealScoreMutation } from "@/app/Relocations/GraphQL/relocationMutations";

/** Matches RelocationRateDealJob::RERATE_QUESTION. */
const RATE_QUESTION =
  "This deal's terms have changed since it was last rated. Call the snapshot tool again and rate it from what it returns.";

type StreamEvent = {
  type: string;
  delta?: string;
  message?: string;
};

export type RelocationDealChat = {
  messages: RelocationDealChatMessage[];
  isLoading: boolean;
  /** Attach to the scrolling thread; it is pinned to the bottom as tokens land. */
  threadRef: RefObject<HTMLDivElement | null>;
  /**
   * Attach to a sentinel at the end of the thread, keyed on the message count,
   * so the newest turn is in view the moment the history loads.
   */
  pinOnMount: (node: HTMLDivElement | null) => void;
  /** The question being answered right now, echoed until the history refetches. */
  pendingQuestion: string | null;
  /** The reply as it arrives, token by token. */
  streamedReply: string;
  isStreaming: boolean;
  error: string | null;
  send: (message: string) => Promise<void>;
  /** Asks for a fresh rating and stores the score it comes back with. */
  rateDeal: () => Promise<void>;
};

export function useRelocationDealChat(
  relocationId: string,
): RelocationDealChat {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  const variables = { relocationId };

  const { data, isLoading } = useGqlQuery(relocationDealChatQuery, variables);

  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [streamedReply, setStreamedReply] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: storeScore } = useGqlMutation(
    refreshRelocationDealScoreMutation,
  );

  function pinToBottom() {
    const thread = threadRef.current;

    if (thread !== null) {
      thread.scrollTop = thread.scrollHeight;
    }
  }

  function pinOnMount(node: HTMLDivElement | null) {
    if (node !== null) {
      requestAnimationFrame(pinToBottom);
    }
  }

  /** Returns whether the answer arrived, since `error` state is a render behind. */
  async function stream(message: string, rerate = false): Promise<boolean> {
    setPendingQuestion(message);
    setStreamedReply("");
    setIsStreaming(true);
    setError(null);

    let failed = false;

    try {
      const response = await fetch(
        new URL(
          `/api/relocations/${relocationId}/deal-chat`,
          import.meta.env.VITE_API_URL,
        ).toString(),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            ...(useAuthStore.getState().token
              ? { Authorization: `Bearer ${useAuthStore.getState().token}` }
              : {}),
            ...(orgId ? { "X-Organisation-Id": orgId } : {}),
          },
          body: JSON.stringify({ message, rerate }),
        },
      );

      if (!response.ok || !response.body) {
        throw new Error(`The analysis failed (${response.status})`);
      }

      await readStream(response.body, (event) => {
        if (event.type === "text_delta" && event.delta) {
          setStreamedReply((reply) => reply + event.delta);
          requestAnimationFrame(pinToBottom);
        }

        if (event.type === "error") {
          failed = true;
          setError(event.message ?? "The analysis failed");
        }
      });

      await queryClient.invalidateQueries({
        queryKey: getQueryKey(relocationDealChatQuery, variables, orgId),
      });
      setPendingQuestion(null);
      setStreamedReply("");
      requestAnimationFrame(pinToBottom);
    } catch (streamError) {
      failed = true;
      setError(
        streamError instanceof Error
          ? streamError.message
          : "The analysis failed",
      );
    } finally {
      setIsStreaming(false);
    }

    return !failed;
  }

  async function send(message: string) {
    await stream(message);
  }

  /**
   * The answer streams into the thread first, then its score is stored, so the
   * number on the listing always comes from an answer staff just watched arrive.
   * It opens a thread of its own: continuing the old one anchors the answer to
   * the figures already quoted rather than the terms as they stand now.
   */
  async function rateDeal() {
    if (!(await stream(RATE_QUESTION, true))) {
      return;
    }

    try {
      await storeScore({ relocationId });
    } catch (storeError) {
      setError(
        storeError instanceof Error
          ? storeError.message
          : "The score could not be saved",
      );
    }
  }

  return {
    messages: data?.relocationDealChat ?? [],
    isLoading,
    threadRef,
    pinOnMount,
    pendingQuestion,
    streamedReply,
    isStreaming,
    error,
    send,
    rateDeal,
  };
}

/**
 * Reads a `text/event-stream` body. Behind API Gateway the whole body can land
 * in one chunk, so events are split off a buffer rather than per read.
 */
async function readStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: StreamEvent) => void,
) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });

    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() ?? "";

    for (const chunk of chunks) {
      const payload = chunk.replace(/^data: /, "").trim();

      if (payload === "" || payload === "[DONE]") {
        continue;
      }

      try {
        onEvent(JSON.parse(payload) as StreamEvent);
      } catch {
        // A partial event: the next read completes it.
        buffer = `${chunk}\n\n${buffer}`;
      }
    }
  }
}
